
import React, { useState, useEffect } from 'react';
import { Ruler, Loader2 } from 'lucide-react';
import { supabase } from '../services/supabase';

interface UnitPreferenceModalProps {
  organizationId: string;
  onConfirm: (system: 'imperial' | 'metric') => void;
}

const STORAGE_KEY = 'sideoutiq_unit_onboarded';

/** Returns true if this org has already completed unit onboarding. */
export function hasCompletedUnitOnboarding(orgId: string): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ids: string[] = JSON.parse(raw);
    return ids.includes(orgId);
  } catch {
    return false;
  }
}

function markUnitOnboardingComplete(orgId: string) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const ids: string[] = raw ? JSON.parse(raw) : [];
    if (!ids.includes(orgId)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids, orgId]));
    }
  } catch {
    // ignore
  }
}

const UnitPreferenceModal: React.FC<UnitPreferenceModalProps> = ({ organizationId, onConfirm }) => {
  const [selected, setSelected] = useState<'imperial' | 'metric'>('imperial');
  const [isSaving, setIsSaving] = useState(false);
  const [isDetecting, setIsDetecting] = useState(true);

  // Ask the server which system to pre-select based on IP country
  useEffect(() => {
    fetch('/api/locale/detect')
      .then(r => r.json())
      .then(data => {
        if (data.suggestedSystem === 'metric' || data.suggestedSystem === 'imperial') {
          setSelected(data.suggestedSystem);
        }
      })
      .catch(() => { /* stay with default */ })
      .finally(() => setIsDetecting(false));
  }, []);

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await supabase
        .from('organizations')
        .update({ unit_preference: selected })
        .eq('id', organizationId);

      markUnitOnboardingComplete(organizationId);
      onConfirm(selected);
    } catch (err) {
      console.error('Failed to save unit preference:', err);
      // Still proceed — the preference is passed up to the parent via onConfirm
      markUnitOnboardingComplete(organizationId);
      onConfirm(selected);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-fade-in-up">
        <div className="p-6 border-b border-slate-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
            <Ruler className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Choose Your Measurement System</h2>
            <p className="text-sm text-slate-500">Sets the default for your entire organization.</p>
          </div>
        </div>

        <div className="p-6 space-y-3">
          {isDetecting ? (
            <div className="flex items-center justify-center py-4 text-slate-400 gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Detecting your region…</span>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setSelected('imperial')}
                className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                  selected === 'imperial'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                  selected === 'imperial' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                }`}>
                  {selected === 'imperial' && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <div>
                  <div className="font-bold text-slate-900">Imperial</div>
                  <div className="text-sm text-slate-500 mt-0.5">Heights in ft/in · weights in lbs · distances in feet</div>
                  <div className="text-xs text-slate-400 mt-1">Net: 7′11⅝″ men · 7′4⅛″ women · Attack line: 10 ft</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelected('metric')}
                className={`w-full flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                  selected === 'metric'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                  selected === 'metric' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                }`}>
                  {selected === 'metric' && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
                <div>
                  <div className="font-bold text-slate-900">Metric</div>
                  <div className="text-sm text-slate-500 mt-0.5">Heights in cm · weights in kg · distances in meters</div>
                  <div className="text-xs text-slate-400 mt-1">Net: 2.43 m men · 2.24 m women · Attack line: 3 m</div>
                </div>
              </button>
            </>
          )}
        </div>

        <div className="p-6 pt-0">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSaving || isDetecting}
            className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Confirm & Continue
          </button>
          <p className="text-xs text-center text-slate-400 mt-3">
            You can change this later in Organization Settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnitPreferenceModal;
